export default {
  headerLinks: [
    { url: "/", text: "Home" },
    { url: "/about", text: "About" },
    { url: "/assessment", text: "Assessment" },
    { url: "/support", text: "Support" },
    { url: "/costs", text: "Costs" },
    { url: "/faqs", text: "FAQs" },
  ],
  sections: {
    titlePage: {
      title: "Autism Diagnosis and Support",
      text: [
        "Autism assessments for children and adults",
        "Support for autistic children and adults and their families",
      ],
      buttonText: "Contact Us",
      buttonLink: "#contact",
    },
    do: {
      id: "do",
      title: "Who are we?",
      text: [
        "Autism Diagnosis and Support (A.D.A.S) is an independent practice based in Kinross, Scotland, which specialises in providing autism assessment and diagnosis for adults and children.",
        "At Autism Diagnosis and Support, we offer autism assessment and diagnosis.",
        "Autistic people and their families need the clarity and understanding that a diagnosis can bring. That understanding can change lives and futures.",
        "As a small clinic, we can be as flexible as a person or family needs – we assess clients in our clinic space in Kinross, in a shared clinic space in Perth, in their school or nursery, or in their own home.",
        "We follow SIGN Guidelines (similar to NICE Guidelines in England), which provide on evidence-based clinical guidelines to ensure best practice. Our clinicians are appropriately trained and qualified, and have many years of experience of assessing and diagnosing autism whilst working in NHS Scotland.",
        "We also offer a variety of support to autistic people and to their families, again adapted and individualised to meet specific needs.",
        "Please note:",
        [
          "- Whilst part of the assessment may be completed online, we will always meet face to face to complete the assessment. We do not do online autism assessment",
          "- We do not assess and diagnose ADHD, although if we pick up, as part of the autism assessment, that ADHD (or another neurodevelopmental condition) is likely, we will discuss this with you and advise accordingly",
        ],
        "We offer a free 20-minute initial consultation call by Zoom or phone. This can be used in any way you choose – to talk about the challenges you and/or your child might be experiencing; to discuss more about the assessment process; to talk about costs and options; to support you to explore whether autism assessment might be beneficial etc.",
        "Please complete the contact form to arrange a free consultation.",
      ],
      image: "hand-shake.png",
    },
    about: {
      id: "about",
      title: "About Us",
      subTitle: "Lynsey Hutchison",
      text: [
        "I graduated as a Speech and Language Therapist in 1987, from Queen Margaret University, Edinburgh. Following this I attended Moray House College of Education in Edinburgh, graduating in 1988 with a Post Graduate Certificate of Education (Primary).",
        "I worked as a speech and language therapist in the NHS from 1989 until 2021, specialising in Autism Spectrum Disorders from 1993. Whilst working within the NHS, I was one of a small number of autonomous diagnosticians providing assessment, diagnosis and intervention for both adults and children with ASD.",
        "In 2017, I registered as an independent speech and language therapist with the Royal College of Speech and Language Therapists and with the Health and Care Professions Council.",
        "Please note:",
        [
          "- I fundamentally believe that healthcare should be free, accessible to all who need it, and publicly funded.",
          "- However the reality is that, Scotland-wide, NHS waiting lists for autism assessments are growing by the week, and some clinics and services have closed their doors completely to new referrals.",
          "- Autistic people and their families need the clarity and understanding that a diagnosis can bring. That understanding can change lives and futures.",
        ],
        // "I have four adult children, three of whom are neurodivergent (ADHD, Dyselxia, Dyscalculia).",
        // "In my early 50s, I was diagnosed with ADHD.",
        // "As an autism assessment requires a multidisciplinary approach (SIGN Guidelines), I work alongside other independent autism-specialist colleagues.",
        // "Additional relevant information",
        // "Please note that I have a diagnosis of ADHD. Whilst this brings me huge strengths and lived experience of neurodivergence, it also brings its own challenges; I strive to ensure that my less-than-perfect executive functioning does not impinge on my work, but there may be occasions when my memory fails me, my timekeeping is poor, procrastination strikes hard, and my reports are delayed…",
        // "If we are working together, please do not EVER hesitate to text or email to remind me of something I said I would do, or to confirm an arrangement we made.",
        // "In addition (and very much as part of my ADHD!) I also live with Chronic Fatigue Syndrome. By its nature, this is unpredictable, and my energy levels and brain power can vary enormously from day to day and hour to hour.",
        // "There may be occasions when I require to postpone appointments at short notice – please be reassured that I only do this when absolutely essential but it does happen more frequently than I would like.",
        // "Thank you in advance, from the bottom of my heart, for your understanding and flexibility.",
      ],
      image: "me3.jpeg",
    },
    assessment1: {
      id: "assessment1",
      title: "Assessment",
      subTitle: "What does assessment for autism consist of?",
      text: [
        "The autism assessment consists of three main parts:",
        [
          "1. Gathering initial information through discussion and questionnaires",
          "2. Autism Diagnostic Observation Schedule (ADOS) – face to face, semi structured ‘interview’",
          "3. Feedback – this may take place immediately after the ADOS, or a day or so later",
        ],
      ],
      image: "question.png",
    },
    assessment2: {
      id: "assessment2",
      subTitle: "What are the benefits of autism assessment and diagnosis?",
      text: [
        "For children, diagnosis can mean that:",
        [
          "- those around the child can begin to understand a bit more about how they think",
          "- the environment can be adapted to better meet their strengths and challenges",
          "- strategies which may help can be identified",
          "- others can better anticipate the child’s developmental trajectory, and plan for potential future challenges",
          "- the child gains increased understanding of themselves and their strengths and learns skills to self-advocate",
        ],
        "For adults, diagnosis can mean that:",
        [
          "- they may better understand themselves and their past challenges",
          "- they can support others to understand their challenges (friends, family, employers etc)",
          "- they can access supports and services which require them to have a diagnosis",
        ],
      ],
      image: "hand-shake.png",
      imageFlip: true,
    },
    support: {
      id: "support",
      title: "Support Services",
      text: [
        [
          "- 1:1 sessions with autistic people and/or their families, to explore and support their understanding around the impact of their diagnosis (regardless of where their diagnosis was made)",
          "- Sessions discussing how their autistic brain works and exploring strategies to maximise this",
          "- Exploring and devising strategies to support families with what they find most challenging, followed by monitoring and evaluation of the strategies implemented",
          "- Sessions with the wider family to support them in understanding their relative’s diagnosis",
          "- Email consultations",
          "- Telephone consultations",
          "- Zoom sessions",
        ],
      ],
      image: "calendar.png",
    },
    support2: {
      id: "support2",
      subTitle: "Some history around intervention",
      text: [
        "In recent years, the ‘landscape’ of autism, and neurodiversity in general, has changed exponentially.",
        "Autism, and neurodivergence in general, is no longer considered a deficit or disorder, but a ‘difference’. A neurodivergent brain has strengths and challenges which are just as valid as a neurotypical brain.",
        "Until very recently, clinicians offered intervention to address these ‘deficits’ which were identified during assessment and diagnosis – the diagnostic criteria for autism unfortunately are still deficit-based – in an attempt to support the autistic person to fit more easily into a neurotypical world.",
        "All that has changed. Intervention now is about supporting autistic people to understand how their brain works, why certain environments can be challenging, and most importantly, how to advocate for themselves.",
      ],
    },
    costs: {
      id: "costs",
      title: "What Does it Cost?",
      table: {
        headers: ["Service", "Price"],
        rows: [
          ["Autism diagnostic assessment", "£1575"],
          [
            "Written suggestions/approaches/strategies for home/school/workplace",
            "£100 per hour \n\n(average time: 2 hours)",
          ],
          [
            "Adaptation of diagnostic report for school/university/workplace",
            "£100 per hour \n\n(average time: 1 hour)",
          ],
          [
            "1:1 direct consultation/support sessions with client or family/other – face to face; Zoom; email; phone call",
            "£100 per hour",
          ],
          [
            "Travel",
            "£40 per hour. \n\n(No charge for locations within 20 minutes of Kinross)",
          ],
        ],
      },
      image: "pound (1).png",
    },
    recommendation: {
      id: "recommendation",
      title: "Recommendations",
      text: [
        {
          name: "Louise",
          text: [
            "I can not recommend Lynsey highly enough. she made so much difference to my son's life and turned him back into the cheerful lad that he had been prior to his exam phobia developing. Her own cheerful demeanour and professional go-to attitude helped to solve many problems.",
          ],
        },
        {
          name: "Mhairi",
          text: [
            "My 13 year old son Joe was diagnosed with combined ADHD at aged 7 and ASD at aged 10. Lynsey worked with him in primary school and high school. Her input and help was so valuable to Joe and myself. Joe has social difficulties with his peers, and at times his anxiety was terrible. Lynsey helped Joe and myself understand the triggers and methods to help. ",
            "Joe also has expressive and receptive language delay. Lynsey helped Joe with methods and set up strategies at school, and for us at home that myself and Joe could understand to help with this. Joe gained a great trust in Lynsey and she helped him feel at ease in understanding day to day school work and life.",
            "Lynsey was so supportive to both Joe and myself especially in dealing with his anxiety and social difficulties. I don't know what I'd have done without her support and advice at times.",
          ],
        },
        {
          name: "Lauren",
          text: [
            "Lynsey was instrumental in getting a diagnosis for my son who has Aspergers. He was only 4 at the time. Lynsey demonstrated wise discernment and clear experience of how ASD kids struggle with stress, and was a valuable advocate to have on board at school meetings etc. Her insight and strong knowledge has proven to be a lifesaver at many a meeting, and other difficult times",
            "Her manner with my son is lovely, not patronising. And her encouragement to me as a parent empowers me to believe I can cope with the difficult days. Lynsey is a wealth of knowledge on Speech & Language techniques and aids. My son is in his teens now, yet I know she will support us into adulthood if need be. I cannot recommend her highly enough.",
          ],
        },
      ],
    },
    contact: {
      id: "contact",
      title: "Contact us",
      text: [
        "Please do not hesitate to contact us to discuss what you might be looking for.",
      ],
      email: "lynseyh39@yahoo.co.uk",
      phone: "+44 7760 412233",
      useForm: "or complete the form",
    },
    faqs: {
      id: "faqs",
      title: "Frequently Asked Questions",
      questions: [
        {
          title: "What does assessment for autism consist of?",
          text: [
            "The autism assessment consists of three main parts:",
            [
              "1. Gathering initial information through discussion and questionnaires e.g. what early developmental and early life was like; what the current challenges are; why might assessment/diagnosis be helpful now; school reports/information from anyone else relevant",
              "2. Autism Diagnostic Observation Schedule (ADOS) – face to face, semi structured ‘interview’",
              "3. Feedback – this may take place immediately after the ADOS, or a day or so later",
            ],
          ],
        },
        {
          title: "What happens after the assessment?",
          text: [
            "I will send you a draft diagnostic report by email, 2-4 weeks after the ADOS. This can be amended and adapted until you are happy with its content and how I have described you/your child.",
            "Further feedback sessions can be arranged if desired, and other family members/relevant people can also be present if you choose for this to happen.",
            "Once finalised, the diagnostic report is emailed to you as a pdf, and a signed copy is sent by post. We suggest that a copy is sent to your GP, but this is not essential.",
          ],
        },
        {
          title: "Where do assessments take place?",
          text: [
            "The initial information-gathering part of the assessment can be face to face, or can take place via email, phone calls or Zoom.",
            "I have my own clinic space in my garden office, in Kinross. This space is accessible to those with physical challenges and aims to provide a comfortable and relaxing environment.",
            "We can also see clients in:",
            [
              "Their own homes",
              "A clinic space in Perth (loaned to us by the Perth and Kinross ADHD Support Group)",
              "One Stop Shop/Number 3 in Perth",
              "School (in specific circumstances)",
            ],
          ],
        },
        {
          title: "How long does the assessment take?",
          text: [
            "If you would like to have a face to face meeting to provide the early and current information, that usually takes around 2-2.5 hours. You may prefer to complete the questionnaires and return them by email and the time taken to complete these varies from family to family. The ADOS takes around 1.5 hours, and feedback may take a further 45 minutes to an hour.",
          ],
        },
        {
          title: "When and how do I pay?",
          text: [
            "Whilst we would like to request a payment in advance of the assessment taking place, we understand that this can be a financial challenge. We will invoice following the agreement of the final report, and this can be paid by bank transfer.",
          ],
        },
      ],
    },
  },
};
